/*
 * Copyright (c) 2021.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Ganturin Vladimir <gun2rin@gmail.com>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {AsyncPipe} from '@angular/common';
import {HttpErrorResponse} from '@angular/common/http';
import {ChangeDetectionStrategy, Component, OnInit} from '@angular/core';
import {ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {NbButtonModule, NbCardModule, NbInputModule, NbWindowRef} from '@nebular/theme';
import {RepairPasswordData, ResetService} from 'compob2b-auth';
import {ApiResponse, BaseComponent, CountDownService, FormatTimePipe, PhoneVerification, SharedModule} from 'compob2b-libs';
import {Observable, Subject} from 'rxjs';
import {map, switchMap, take, takeUntil, tap} from 'rxjs/operators';
import {validatePhone} from '../../cabinet/utils/validate-phone';
import {TuiIcon} from '@taiga-ui/core'

const MIN_LENGTH = 6;

@Component({
  selector: 'app-popup-forgot',
  templateUrl: './forgot-sms.component.html',
  imports: [
    ReactiveFormsModule,
    SharedModule,
    NbButtonModule,
    AsyncPipe,
    NbCardModule,
    NbInputModule,
    FormatTimePipe,
    TuiIcon
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ForgotPasswordSmsComponent extends BaseComponent implements OnInit {
  codeValidated: Observable<boolean>;
  isValidForm: Observable<boolean>;
  counter: number;
  counterStarted = false;
  smsCodeFieldOpened = false;
  attemptError = false;
  timeLeft: number;
  isValidPassFields: Subject<boolean>;
  phoneCheckIsDisabled = false;
  verifyError = false;
  verifyErrorData = '';
  private tick = 1000;

  /**
   * FormGroup - group of form elements (inputs) with simple validation
   * {@link https://angular.io/guide/forms-overview#common-foundation}
   * {@link https://angular.io/guide/form-validation}
   */
  forgotForm = new UntypedFormGroup({
    phone: new UntypedFormControl('', [Validators.required, validatePhone(11)]),
    smsCode: new UntypedFormControl('', [Validators.required, Validators.pattern('^(\\d{5})$')]),
    password: new UntypedFormControl('', [Validators.required, Validators.minLength(MIN_LENGTH)]),
    repeatPassword: new UntypedFormControl('', [Validators.required, Validators.minLength(MIN_LENGTH)]),
  });

  constructor(protected readonly windowRef: NbWindowRef,
              private readonly countDownService: CountDownService,
              private readonly resetService: ResetService
  ) {
    super();
  }

  ngOnInit() {
    this.isValidPassFields = this.resetService.passFields();
    this.isValidForm = this.resetService.validate(this.forgotForm);
  }

  close(): void {
    this.windowRef.close();
  }

  checkNumber(): void {
    this.attemptError = false;
    this.verifyError = false;
    this.verifyErrorData = '';
    this.codeValidated = null;
    this.forgotForm.controls.smsCode.patchValue('');
    this.forgotForm.controls.smsCode.markAsUntouched();

    let onlyNumeric = this.forgotForm.controls.phone.value.replace(/\D/g, '');

    if (onlyNumeric.length > 11) {
      onlyNumeric = onlyNumeric.substring(0, 11);
    }

    this.compoApiService.post<PhoneVerification, number>(
      '/sms/phone/forgot/validate', {phone: '7' + onlyNumeric.substring(1)}
    ).pipe(
        map(response => response.data),
        switchMap(seconds => this.startCountDown(seconds)),
        take(1)
      )
      .subscribe({
        next: () => {},
        error: (error: HttpErrorResponse) => this.handleVerifyError(error),
      });
  }

  protected startCountDown(seconds: number): Observable<number> {
    this.counter = seconds;
    this.startCounter();
    return this.countDownService.getTimer(this.tick)
      .pipe(
        tap(_ => this.counter === 0 ? this.endCounter() : null),
        tap(_ => --this.counter),
        tap(_ => this.cdr.detectChanges())
      );
  }

  private startCounter(): void {
    this.counterStarted = true;
    this.smsCodeFieldOpened = true;
    this.phoneCheckIsDisabled = true;
    this.forgotForm.controls.phone.disable();
    this.cdr.detectChanges();
  }

  private endCounter(): void {
    this.counterStarted = false;
    this.attemptError = false;
    this.verifyError = false;
    this.verifyErrorData = '';
    this.phoneCheckIsDisabled = false;
    this.forgotForm.controls.phone.enable();
    this.cdr.detectChanges();
  }

  /**
   * Handles failed result of registration request
   */
  private errorHandler(): void {
    this.errorToast($localize`:@@PasswordRecoveryError:Возникла ошибка`);
    this.loading = false;
    this.cdr.detectChanges();
  }

  /**
   * Handles failed phone verification request
   * @param errorResponse
   */
  handleVerifyError(errorResponse: HttpErrorResponse): void {
    if (errorResponse.status === 404) {
      this.attemptError = false;
      this.verifyError = true;
      this.verifyErrorData = errorResponse.error.data;
    } else {
      this.attemptError = true;
      this.timeLeft = errorResponse.error.data;
      this.verifyError = false;
      this.verifyErrorData = '';
    }
    this.cdr.detectChanges();
  }

  checkSmsCode(): void {
    this.loading = true;

    this.codeValidated = this.compoApiService.post<{
        code: string
      }, boolean>('/sms/code/validate', {code: this.forgotForm.controls.smsCode.value})
      .pipe(
        map(response => response.data),
        tap(result => result === true ? this.forgotForm.controls.phone.enable() : null),
        tap(_ => this.loading = false)
      );
  }

  /**
   * Handles success result of request
   * @param response ApiResponse
   */
  private successHandler(response: ApiResponse<string>): void {
    this.loading = false;
    this.successToast($localize`:@@PasswordSuccessfullyChanged:Пароль успешно изменен`);
    this.windowRef.close();
  }

  onSubmit(): void {
    if (this.forgotForm.valid) {
      this.loading = true;

      let onlyNumeric = this.forgotForm.controls.phone.value.replace(/\D/g, '');

      if (onlyNumeric.length > 11) {
        onlyNumeric = onlyNumeric.substring(0, 11);
      }
      const normalizedPhone = '7' + onlyNumeric.substring(1);

      const data: RepairPasswordData = {
        phone: normalizedPhone,
        password: this.forgotForm.controls.password.value,
        repeatPassword: this.forgotForm.controls.repeatPassword.value,
        smsCode: this.forgotForm.controls.smsCode.value
      };

      this.compoApiService.patch<RepairPasswordData, string>('/repair-password-with-sms', data)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe({
          next: (response: ApiResponse<string>) => this.successHandler(response),
          error: () => this.errorHandler(),
        });
    }
  }
}
